import { useEffect, useRef, useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { useAuthStore } from "@/stores/auth";
import { useMeQuery, useRequestEmailChangeMutation } from "@/api/auth";
import {
  useModelPrefsQuery,
  useSetModelPrefsMutation,
  useStyleProfileQuery,
  useSetStyleProfileMutation,
} from "@/api/settings";
import { ApiError } from "@/api/client";
import { uploadAsset, deleteAsset } from "@/api/assets";
import { useTenantFonts, reloadTenantFonts } from "@/lib/tenantFonts";
import { ClientsCard } from "@/components/settings/ClientsCard";
import { LocalRenderCard } from "@/components/settings/LocalRenderCard";
import { ProviderCredentialsCard } from "@/components/settings/ProviderCredentialsCard";
import {
  useAvailableModelsQuery,
  type AvailableModel,
} from "@/api/settings";
import { sortModels, groupModelsByFamily } from "@/lib/modelSort";
import { ModelCombobox } from "@/components/ModelCombobox";

const MODEL_SOURCE_LABELS: Record<AvailableModel["source"], string> = {
  api_key: "Your API key",
  platform_key: "Platform key",
  claude_account: "Your Claude account",
  server_cli: "Server Claude login",
  gemini_key: "Gemini",
};

const CASCADE_PROVIDER_LABELS: Record<string, string> = {
  claude: "Anthropic",
  openai: "OpenAI",
  opencode: "OpenCode",
};

/** Provider + model cascade, populated exactly like the composer picker:
 *  provider first, then that provider's models grouped by family. */
function ProviderModelCascade({
  provider,
  onProvider,
  model,
  onModel,
  models,
  providers,
  availability,
}: {
  provider: string;
  onProvider: (p: string) => void;
  model: string;
  onModel: (m: string) => void;
  models: AvailableModel[];
  providers: string[];
  availability?: import("@/api/settings").AvailableModels;
}) {
  const serverProvider = availability?.default_provider || "claude";
  const effective = provider || serverProvider;
  const filtered = models.filter((m) => m.provider === effective);
  const groups = groupModelsByFamily(filtered);
  const orphaned = model !== "" && !filtered.some((m) => m.id === model);
  // Captions always name the EFFECTIVE choice — explicit picks included.
  const providerCaption = CASCADE_PROVIDER_LABELS[effective] ?? effective;
  const modelCaption = (() => {
    if (model !== "") return model;
    if (effective === "openai") return availability?.openai_default ?? "account default";
    if (effective === "opencode") return availability?.opencode_default ?? "account default";
    let resolved = availability?.claude_default;
    if (resolved && !/\d/.test(resolved)) {
      const alias = resolved;
      const concrete = sortModels(
        filtered.filter((m) => /\d/.test(m.id)),
      ).find((m) => m.id.includes(alias));
      if (concrete) resolved = concrete.id;
    }
    return resolved ?? " ";
  })();
  return (
    <div className="flex gap-2">
      <div className="flex w-40 flex-col gap-0.5">
      <span className="min-h-[14px] truncate pl-1 text-[10px] leading-[14px] text-ink-3" title={providerCaption}>
        {providerCaption}
      </span>
      <select
        value={provider}
        onChange={(e) => onProvider(e.target.value)}
        className="w-40 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-line-strong focus:outline-none"
        aria-label="Default provider"
      >
        <option value="">Auto</option>
        {providers.map((p) => (
          <option key={p} value={p}>
            {CASCADE_PROVIDER_LABELS[p] ?? p}
          </option>
        ))}
      </select>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="min-h-[14px] truncate pl-1 text-[10px] leading-[14px] text-ink-3" title={modelCaption}>
        {modelCaption}
      </span>
      <ModelCombobox value={model} onChange={onModel} models={filtered} autoHint={model === "" ? modelCaption : undefined} />
      </div>
    </div>
  );
}

/** Select grouped by the auth source that grants each model. Keeps a saved
 *  value visible (flagged) even if it's absent from today's live list, so
 *  the control never silently jumps selections. */
function GroupedModelSelect({
  value,
  onChange,
  models,
}: {
  value: string;
  onChange: (v: string) => void;
  models: AvailableModel[];
}) {
  const sources = [...new Set(models.map((m) => m.source))];
  const orphaned = value !== "" && !models.some((m) => m.id === value);
  // Providers reuse display names across variants (e.g. a GA and a preview
  // model both called "Nano Banana Pro") — append the id when ambiguous.
  const labelCounts = new Map<string, number>();
  for (const m of models) {
    labelCounts.set(m.label, (labelCounts.get(m.label) ?? 0) + 1);
  }
  const optionText = (m: AvailableModel) =>
    (labelCounts.get(m.label) ?? 0) > 1 ? `${m.label} (${m.id})` : m.label;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-line-strong focus:outline-none"
    >
      <option value="">Server default</option>
      {sources.map((source) => (
        <optgroup key={source} label={MODEL_SOURCE_LABELS[source] ?? source}>
          {sortModels(models.filter((m) => m.source === source))
            .map((m) => (
              <option key={m.id} value={m.id}>
                {optionText(m)}
              </option>
            ))}
        </optgroup>
      ))}
      {orphaned && (
        <option value={value}>{value} (saved — not in current list)</option>
      )}
    </select>
  );
}

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const { data: freshUser } = useMeQuery();
  const displayUser = freshUser ?? user;

  // Email change state
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const requestEmailChange = useRequestEmailChangeMutation();

  const currentEmail = displayUser?.email ?? null;

  // Model preferences state
  const { data: modelPrefs } = useModelPrefsQuery();
  const setModelPrefs = useSetModelPrefsMutation();
  const [remotionModel, setRemotionModel] = useState("");
  const [remotionProvider, setRemotionProvider] = useState("");
  const [svgModel, setSvgModel] = useState("");
  const [svgProvider, setSvgProvider] = useState("");
  const [imageModel, setImageModel] = useState("");

  // Seed the selects from the query once it loads. Mirror late-arriving
  // props via useEffect so a re-render doesn't clobber in-progress edits.
  useEffect(() => {
    if (!modelPrefs) return;
    setRemotionModel(modelPrefs.remotion_model);
    setRemotionProvider(modelPrefs.remotion_provider ?? "");
    setSvgModel(modelPrefs.svg_model);
    setSvgProvider(modelPrefs.svg_provider ?? "");
    setImageModel(modelPrefs.image_model);
  }, [modelPrefs]);

  // Brand style profile state
  const { data: styleProfile } = useStyleProfileQuery();
  const setStyleProfile = useSetStyleProfileMutation();
  const [styleArtStyle, setStyleArtStyle] = useState("");
  const [styleMotion, setStyleMotion] = useState("");
  const [stylePalette, setStylePalette] = useState("");
  const [styleTypography, setStyleTypography] = useState("");
  const [styleNotes, setStyleNotes] = useState("");

  // Mirror the late-arriving query into editable state (same pattern as
  // model prefs above).
  useEffect(() => {
    if (!styleProfile) return;
    setStyleArtStyle(styleProfile.art_style ?? "");
    setStyleMotion(styleProfile.motion ?? "");
    setStylePalette(styleProfile.palette ?? "");
    setStyleTypography(styleProfile.typography ?? "");
    setStyleNotes(styleProfile.notes ?? "");
  }, [styleProfile]);

  const handleSaveStyleProfile = () => {
    setStyleProfile.mutate({
      art_style: styleArtStyle,
      motion: styleMotion,
      palette: stylePalette,
      typography: styleTypography,
      notes: styleNotes,
    });
  };

  // Brand fonts state
  const tenantFonts = useTenantFonts();
  const fontInputRef = useRef<HTMLInputElement>(null);
  const [fontUploading, setFontUploading] = useState(false);
  const [fontError, setFontError] = useState<string | null>(null);
  const [deletingFontId, setDeletingFontId] = useState<string | null>(null);

  // Ensure the list (and faces, so names render in their own font) is loaded
  // when the page opens.
  useEffect(() => {
    void reloadTenantFonts();
  }, []);

  const extractError = (err: unknown, fallback: string): string => {
    if (err instanceof ApiError) {
      const body = err.body as { error?: unknown; message?: unknown } | null;
      if (body && typeof body.error === "string") return body.error;
      if (body && typeof body.message === "string") return body.message;
      return err.message;
    }
    if (err instanceof Error) return err.message;
    return fallback;
  };

  const handleFontFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file still fires onChange.
    e.target.value = "";
    if (!file) return;
    setFontError(null);
    setFontUploading(true);
    try {
      await uploadAsset(file, "font");
      await reloadTenantFonts();
    } catch (err) {
      setFontError(extractError(err, "Font upload failed"));
    } finally {
      setFontUploading(false);
    }
  };

  const handleDeleteFont = async (assetId: string, family: string) => {
    if (!window.confirm(`Remove the brand font "${family}"?`)) return;
    setFontError(null);
    setDeletingFontId(assetId);
    try {
      await deleteAsset(assetId);
      await reloadTenantFonts();
    } catch (err) {
      setFontError(extractError(err, "Failed to remove font"));
    } finally {
      setDeletingFontId(null);
    }
  };

  const handleEmailChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !currentPassword) return;
    requestEmailChange.mutate(
      { new_email: newEmail.trim(), current_password: currentPassword },
      {
        onSuccess: () => {
          setEmailSubmitted(true);
          setCurrentPassword("");
        },
      },
    );
  };

  const handleSaveModelPrefs = () => {
    setModelPrefs.mutate({
      remotion_model: remotionModel,
      remotion_provider: remotionProvider,
      svg_model: svgModel,
      svg_provider: svgProvider,
      image_model: imageModel,
    });
  };

  // What this user's auth actually grants — live-queried from the providers.
  const { data: availableModels } = useAvailableModelsQuery();
  // Fallback aliases while loading / if every probe fails: the CLI resolves
  // these regardless of auth source.
  const textModels: AvailableModel[] = availableModels?.text?.length
    ? availableModels.text
    : [
        { id: "opus", label: "Claude Opus", provider: "claude", source: "server_cli" as const },
        { id: "sonnet", label: "Claude Sonnet", provider: "claude", source: "server_cli" as const },
        { id: "haiku", label: "Claude Haiku", provider: "claude", source: "server_cli" as const },
      ];
  const imageModels: AvailableModel[] = availableModels?.image ?? [];

  return (
    <Layout>
      <div className="mx-auto max-w-2xl p-6">
        <h1 className="mb-6 text-2xl font-semibold text-ink">
          Settings
        </h1>

        <div className="rounded-xl border border-line bg-surface-2">
          {/* Profile */}
          <div className="border-b border-line px-6 py-4">
            <h2 className="text-sm font-semibold text-ink">
              Profile
            </h2>
          </div>
          <div className="space-y-4 p-6">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Username
              </label>
              <div className="text-sm text-ink">
                {displayUser?.username ?? "—"}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Role
              </label>
              <span className="text-sm text-ink-2">
                {displayUser?.role ?? "user"}
              </span>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Email
              </label>
              <div className="text-sm text-ink">
                {currentEmail ?? (
                  <span className="text-warning">
                    no email on file — add one to enable password recovery
                  </span>
                )}
              </div>
            </div>

            {emailSubmitted ? (
              <div className="mt-2 rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-xs text-success">
                Verification email sent. Click the link in your new inbox
                to confirm the change. Your email won't update until then.
              </div>
            ) : (
              <form
                onSubmit={handleEmailChange}
                className="mt-2 space-y-3 border-t border-line pt-4"
              >
                <div className="text-sm font-medium text-ink-2">
                  {currentEmail ? "Change email" : "Add email"}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink-2">
                    New email
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink-2">
                    Current password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                  />
                </div>
                {requestEmailChange.error && (
                  <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                    {requestEmailChange.error.message}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={
                    !newEmail.trim() ||
                    !currentPassword ||
                    requestEmailChange.isPending
                  }
                  className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {requestEmailChange.isPending
                    ? "Sending..."
                    : "Send verification"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Models — per-user model overrides for the generation pipeline. */}
        <div className="mt-6 rounded-xl border border-line bg-surface-2">
          <div className="border-b border-line px-6 py-4">
            <h2 className="text-sm font-semibold text-ink">Models</h2>
          </div>
          <div className="space-y-4 p-6">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Motion graphics generation
              </label>
              <ProviderModelCascade
                provider={remotionProvider}
                onProvider={(p) => { setRemotionProvider(p); setRemotionModel(""); }}
                model={remotionModel}
                onModel={setRemotionModel}
                models={textModels}
                providers={availableModels?.providers ?? ["claude"]}
                availability={availableModels}
              />
              <p className="mt-1 text-xs text-ink-3">
                Model that writes Remotion templates.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                SVG generation
              </label>
              <ProviderModelCascade
                provider={svgProvider}
                onProvider={(p) => { setSvgProvider(p); setSvgModel(""); }}
                model={svgModel}
                onModel={setSvgModel}
                models={textModels}
                providers={availableModels?.providers ?? ["claude"]}
                availability={availableModels}
              />
              <p className="mt-1 text-xs text-ink-3">
                Model that designs SVG graphics.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Image restyle
              </label>
              <GroupedModelSelect
                value={imageModel}
                onChange={setImageModel}
                models={imageModels}
              />
              <p className="mt-1 text-xs text-ink-3">
                Image model used by Image to vector to redraw (pixel art, style
                changes).
                {imageModels.length === 0 &&
                  " No Gemini key detected — add one (or use the server's) to enable restyle models."}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveModelPrefs}
                disabled={setModelPrefs.isPending}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                {setModelPrefs.isPending ? "Saving..." : "Save Models"}
              </button>
            </div>

            {setModelPrefs.isError && (
              <p className="text-xs text-danger">
                Failed to save model preferences. Please try again.
              </p>
            )}
            {setModelPrefs.isSuccess && (
              <p className="text-xs text-success">
                Model preferences saved.
              </p>
            )}

            <p className="text-xs text-ink-3">
              Changes apply to new sessions; an in-flight session keeps the
              model it started with.
            </p>
          </div>
        </div>

        {/* Brand style — tenant defaults injected into every generation so
            the first attempt lands on-brand. Projects can opt out or
            override per-session in the workspace Settings tab. */}
        <div className="mt-6 rounded-xl border border-line bg-surface-2">
          <div className="border-b border-line px-6 py-4">
            <h2 className="text-sm font-semibold text-ink">Brand style</h2>
          </div>
          <div className="space-y-4 p-6">
            <p className="text-xs text-ink-3">
              Describe your team's house style. Every generation follows these
              defaults unless a project overrides them (workspace → Settings →
              Brand style), so the stuff you'd otherwise ask for every time is
              baked in.
            </p>
            {(
              [
                {
                  key: "art_style",
                  label: "Art style",
                  placeholder:
                    "e.g. synthwave/neon — deep purple-black backgrounds, dominant pink, cyan accents",
                  value: styleArtStyle,
                  set: setStyleArtStyle,
                },
                {
                  key: "motion",
                  label: "Motion & timing",
                  placeholder:
                    "e.g. snappy modern easing, arrive in under 0.5s, subtle idle motion during holds",
                  value: styleMotion,
                  set: setStyleMotion,
                },
                {
                  key: "palette",
                  label: "Palette",
                  placeholder: "e.g. #080018 base, #ff1493 primary, #00d4ff accent",
                  value: stylePalette,
                  set: setStylePalette,
                },
                {
                  key: "typography",
                  label: "Typography",
                  placeholder: "e.g. Bebas Neue for headlines, all-caps, tight tracking",
                  value: styleTypography,
                  set: setStyleTypography,
                },
              ] as const
            ).map((f) => (
              <div key={f.key}>
                <label className="mb-1 block text-sm font-medium text-ink-2">
                  {f.label}
                </label>
                <input
                  type="text"
                  value={f.value}
                  onChange={(e) => f.set(e.target.value)}
                  maxLength={500}
                  placeholder={f.placeholder}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3/60 focus:border-line-strong focus:outline-none"
                />
              </div>
            ))}
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Notes
              </label>
              <textarea
                value={styleNotes}
                onChange={(e) => setStyleNotes(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Anything else the generator should always know — themes to avoid, brand voice, layout habits…"
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3/60 focus:border-line-strong focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveStyleProfile}
                disabled={setStyleProfile.isPending}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                {setStyleProfile.isPending ? "Saving..." : "Save Brand Style"}
              </button>
            </div>
            {setStyleProfile.isError && (
              <p className="text-xs text-danger">
                Failed to save brand style. Please try again.
              </p>
            )}
            {setStyleProfile.isSuccess && (
              <p className="text-xs text-success">Brand style saved.</p>
            )}
          </div>
        </div>

        {/* Clients — per-client brand/style profiles, selectable per project. */}
        <ClientsCard />

        {/* Local rendering — one-paste agent setup with live status. */}
        <LocalRenderCard />

        {/* Model providers — one section: pick provider, then auth method
            (API key or subscription login). */}
        <ProviderCredentialsCard />

        {/* Brand Fonts */}
        <div className="mt-6 rounded-xl border border-line bg-surface-2">
          <div className="flex items-center justify-between border-b border-line px-6 py-4">
            <h2 className="text-sm font-semibold text-ink">Brand fonts</h2>
            <button
              onClick={() => fontInputRef.current?.click()}
              disabled={fontUploading}
              className="rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              {fontUploading ? "Uploading..." : "Upload font"}
            </button>
            <input
              ref={fontInputRef}
              type="file"
              accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
              onChange={handleFontFile}
              className="hidden"
            />
          </div>
          <div className="space-y-4 p-6">
            <p className="text-xs text-ink-3">
              Upload your own .ttf, .otf, .woff, or .woff2 files (max 8MB each).
              They become available in the font picker for every template and
              are shared with your team automatically.
            </p>

            {fontError && (
              <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                {fontError}
              </div>
            )}

            {tenantFonts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink-3">
                No brand fonts yet. Upload one to use it across your templates.
              </div>
            ) : (
              <ul className="space-y-2">
                {tenantFonts.map((font) => (
                  <li
                    key={font.asset_id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-3 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className="truncate text-base text-ink"
                        style={{ fontFamily: `'${font.family}'` }}
                        title={font.family}
                      >
                        {font.family}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span className="rounded bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium uppercase text-ink-3">
                          {font.weight}
                        </span>
                        <span className="rounded bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium uppercase text-ink-3">
                          {font.style || "normal"}
                        </span>
                        <span className="rounded bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium uppercase text-ink-3">
                          {font.format}
                        </span>
                        <span className="truncate text-[11px] text-ink-3">
                          {font.filename ?? ""}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteFont(font.asset_id, font.family)}
                      disabled={deletingFontId === font.asset_id}
                      className="shrink-0 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-danger transition-colors hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {deletingFontId === font.asset_id ? "Removing..." : "Remove"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mt-6 rounded-xl border border-danger/30 bg-surface-2">
          <div className="border-b border-danger/30 px-6 py-4">
            <h2 className="text-sm font-semibold text-danger">
              Danger Zone
            </h2>
          </div>
          <div className="p-6">
            <p className="mb-4 text-xs text-ink-2">
              Deleting your account will remove all templates, renders, and
              data. This cannot be undone.
            </p>
            <button className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft">
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
